import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, orderBy, query } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Community } from "../../types/index"; 
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";
import { Building, MapPin, Edit2, Trash2, Plus, Check, X } from "lucide-react";
import { Spinner } from "../../components/common/Spinner";

export const CommunitiesSection: React.FC = () => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Default Form State
  const initialFormState: Partial<Community> = {
    name: "",
    shortId: "",
    area: "",
    city: "Bengaluru",
    pincode: "",
    level1: { id: "l1", label: "Block", values: [] }, 
    level2: { id: "l2", label: "Floor", values: [] },
    level3: { id: "l3", label: "Flat", prefixLevel1: false, digitsCount: 0 },
  };

  const [formData, setFormData] = useState<Partial<Community>>(initialFormState);

  // 1. Fetch Communities
  const fetchCommunities = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "communities"), orderBy("name"));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Community));
      setCommunities(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load communities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCommunities(); }, []);

  // 2. Handle Save (Create or Update)
  const handleSave = async () => {
    if (!formData.name || !formData.shortId) return toast.error("Name and Short ID are required");

    try {
      if (formData.id) {
        // Update
        await updateDoc(doc(db, "communities", formData.id), formData);
        toast.success("✅ Community Updated");
      } else {
        // Create
        await addDoc(collection(db, "communities"), {
          ...formData,
          createdAt: new Date(),
          status: 'active'
        });
        toast.success("✅ Community Created");
      }
      setIsEditing(false);
      setFormData(initialFormState);
      fetchCommunities();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save");
    }
  };

  const handleEdit = (comm: Community) => {
    setFormData(comm);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if(!window.confirm("⚠️ Delete this community? Users with addresses here will lose their mapping.")) return;
    try {
        await deleteDoc(doc(db, "communities", id));
        toast.success("Community deleted");
        fetchCommunities();
    } catch(err) {
        toast.error("Failed to delete");
    }
  };

  if (loading && !isEditing) return <div className="p-12 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
          <Building className="text-orange-600" /> Community Manager
        </h2>
        {!isEditing && (
            <Button onClick={() => { setFormData(initialFormState); setIsEditing(true); }}>
            <Plus size={18} className="mr-2"/> Add Community
            </Button>
        )}
      </div>

      {/* EDIT FORM */}
      {isEditing ? (
        <Card className="p-6 max-w-3xl mx-auto border-t-4 border-t-orange-500 animate-in fade-in">
          <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">{formData.id ? "Edit Community" : "New Community"}</h3>
              <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-gray-100 rounded-full"><X/></button>
          </div>
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <Input label="Community Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Prestige Shantiniketan" />
            <Input label="Short Code (Unique)" value={formData.shortId} onChange={e => setFormData({...formData, shortId: e.target.value.toUpperCase()})} placeholder="e.g. PSN" />
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <Input label="Area" value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} />
            <Input label="City" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
            <Input label="Pincode" value={formData.pincode} onChange={e => setFormData({...formData, pincode: e.target.value})} />
          </div>

          {/* Structure Configuration */}
          <div className="bg-blue-50 p-5 rounded-xl border border-blue-100 mb-8">
            <div className="flex items-center gap-2 mb-4">
                <Building size={18} className="text-blue-600"/>
                <h4 className="font-bold text-sm text-blue-800 uppercase">Address Structure</h4>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Level 1 (Grouping)</label>
                <input 
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-blue-500 outline-none" 
                  value={formData.level1?.label} 
                  onChange={e => setFormData({...formData, level1: { ...formData.level1!, label: e.target.value }})}
                  placeholder="e.g. Block / Tower"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Level 2 (Vertical)</label>
                <input 
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-blue-500 outline-none" 
                  value={formData.level2?.label} 
                  onChange={e => setFormData({...formData, level2: { ...formData.level2!, label: e.target.value }})}
                  placeholder="e.g. Floor"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Level 3 (Unit)</label>
                <input 
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-blue-500 outline-none" 
                  value={formData.level3?.label} 
                  onChange={e => setFormData({...formData, level3: { ...formData.level3!, label: e.target.value }})}
                  placeholder="e.g. Flat / Villa"
                />
              </div>
            </div>
            
            <div>
               <label className="text-xs font-bold text-gray-500 block mb-1">Pre-defined Blocks (comma separated)</label>
               <input 
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-blue-500 outline-none" 
                  placeholder="A, B, C, D..."
                  defaultValue={formData.level1?.values?.join(", ")}
                  onBlur={e => setFormData({...formData, level1: { ...formData.level1!, values: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }})}
               />
               <p className="text-[10px] text-gray-500 mt-1">Users will see these options in a dropdown.</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button onClick={handleSave} className="px-8 bg-orange-600 hover:bg-orange-700">Save Community</Button>
          </div>
        </Card>
      ) : (
        /* LIST VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communities.length === 0 ? (
             <div className="col-span-3 text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                 <Building size={48} className="mx-auto text-gray-300 mb-2"/>
                 <p className="text-gray-500 font-medium">No communities found</p>
             </div>
          ) : (
             communities.map(comm => (
                <Card key={comm.id} className="p-0 overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="bg-orange-50 p-4 border-b border-orange-100 flex justify-between items-start">
                    <div>
                        <h3 className="font-bold text-lg text-gray-900">{comm.name}</h3>
                        <span className="text-xs font-bold bg-white text-orange-600 px-2 py-0.5 rounded border border-orange-200 mt-1 inline-block">
                            {comm.shortId}
                        </span>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                        <button onClick={() => handleEdit(comm)} className="p-1.5 bg-white text-blue-600 rounded-lg shadow-sm hover:bg-blue-50"><Edit2 size={16}/></button>
                        <button onClick={() => handleDelete(comm.id)} className="p-1.5 bg-white text-red-600 rounded-lg shadow-sm hover:bg-red-50"><Trash2 size={16}/></button>
                    </div>
                </div>
                
                <div className="p-4 space-y-3">
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin size={16} className="mt-0.5 shrink-0 text-gray-400"/>
                        {comm.area}, {comm.city} - {comm.pincode}
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1 border border-gray-100">
                        <div className="flex justify-between">
                            <span className="text-gray-500 font-medium">Groups:</span>
                            <span className="font-bold text-gray-800">{comm.level1.label}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 font-medium">Verticals:</span>
                            <span className="font-bold text-gray-800">{comm.level2.label}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 font-medium">Units:</span>
                            <span className="font-bold text-gray-800">{comm.level3.label}</span>
                        </div>
                    </div>

                    <div className="pt-2 flex gap-2">
                        <div className="flex-1 bg-blue-50 text-blue-700 text-xs font-bold py-1.5 px-3 rounded-lg text-center">
                            {comm.level1.values?.length || 0} Blocks
                        </div>
                        <div className="flex-1 bg-green-50 text-green-700 text-xs font-bold py-1.5 px-3 rounded-lg text-center">
                             Active
                        </div>
                    </div>
                </div>
                </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CommunitiesSection;